import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  Drawer,
  EmptyState,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Tabs,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { suppliersApi } from '../suppliers/suppliersApi';
import { settingsApi } from '../settings/settingsApi';
import { refFromSlug, itemPath } from '../../lib/slug';
import { catalogApi, itemStatusMeta } from './catalogApi';
import ItemForm from './ItemForm';
import ItemSuppliersTab from './ItemSuppliersTab';
import ParLevelsTab from './ParLevelsTab';
import PurchaseHistoryTab from './PurchaseHistoryTab';
import './catalog.css';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'suppliers', label: 'Suppliers & pricing' },
  { id: 'purchases', label: 'Purchase history' },
  { id: 'parlevels', label: 'Par levels' },
];

const priceRange = (lo, hi) => {
  if (!lo) return '—';
  if (!hi || lo.amount === hi.amount) return money(lo);
  if (lo.currency === hi.currency) return `${lo.currency} ${formatAmount(lo.amount)} – ${formatAmount(hi.amount)}`;
  return `${money(lo)} – ${money(hi)}`;
};

function toUpdatePayload(item, overrides = {}) {
  return {
    id: item.id,
    name: item.name,
    sku: item.sku ?? null,
    barcode: item.barcode ?? null,
    description: item.description ?? null,
    status: item.status,
    categoryId: item.categoryId ?? null,
    stockUomId: item.stockUomId ?? null,
    isStockTracked: item.isStockTracked,
    taxRatePercent: item.taxRatePercent ?? null,
    tags: item.tags ?? [],
    preferredSupplierId: item.preferredSupplierId ?? null,
    defaultCostCenter: item.defaultCostCenter ?? null,
    defaultGlAccountCode: item.defaultGlAccountCode ?? null,
    standardCost: item.standardCost ?? null,
    parLevels: (item.parLevels ?? []).map((p) => ({
      locationId: p.locationId,
      parLevel: p.parLevel,
      reorderPoint: p.reorderPoint,
      reorderQuantity: p.reorderQuantity ?? null,
    })),
    ...overrides,
  };
}

export default function ItemDetailPage() {
  const { id: routeRef } = useParams();
  const id = refFromSlug(routeRef);
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission } = useProperty();
  const canManage = hasPermission('catalog.manage');

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const fileInputRef = useRef(null);

  const [tab, setTab] = useState('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadItem = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await catalogApi.getItem(id);
      setItem(res);
    } catch (err) {
      setError(err?.message || t('Failed to load item.'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const loadStats = useCallback(async (itemId) => {
    try { setStats(await catalogApi.itemStats(itemId)); } catch { /* non-fatal */ }
  }, []);

  const loadImage = useCallback(async (itemId) => {
    try {
      const res = await catalogApi.getItemImageUrl(itemId);
      setImageUrl(res?.url || null);
    } catch { setImageUrl(null); }
  }, []);

  useEffect(() => { loadItem(); }, [loadItem]);

  // Once the item resolves, key stats/image off its real id and canonicalise the URL.
  useEffect(() => {
    if (!item?.id) return;
    loadStats(item.id);
    loadImage(item.id);
    const canonical = itemPath(item);
    if (`/catalog/items/${routeRef}` !== canonical) {
      navigate(canonical, { replace: true });
    }
  }, [item, routeRef, loadStats, loadImage, navigate]);

  const onPickImage = () => fileInputRef.current?.click();

  const onImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImageBusy(true);
    try {
      const updated = await catalogApi.uploadItemImage(item.id, file);
      setItem(updated);
      await loadImage(item.id);
      toast.success(t('Image updated'));
    } catch (err) {
      toast.error(t('Could not upload image'), { description: err?.message });
    } finally {
      setImageBusy(false);
    }
  };

  const removeImage = async () => {
    setImageBusy(true);
    try {
      const updated = await catalogApi.removeItemImage(item.id);
      setItem(updated);
      setImageUrl(null);
      toast.success(t('Image removed'));
    } catch (err) {
      toast.error(t('Could not remove image'), { description: err?.message });
    } finally {
      setImageBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [cats, us, locs, sup, eff] = await Promise.all([
          catalogApi.listCategories(),
          catalogApi.listUnits(),
          catalogApi.listLocations(),
          suppliersApi.list({ page: 1, pageSize: 200 }),
          settingsApi.getEffective().catch(() => null),
        ]);
        setCategories(cats || []);
        setUnits(us || []);
        setLocations(locs || []);
        setSuppliers(sup?.items || []);
        setCostCenters(eff?.costCenters || []);
        setGlAccounts(eff?.glAccounts || []);
      } catch {
        // Lookups are best-effort.
      }
    })();
  }, []);

  const catName = (cid) => categories.find((c) => c.id === cid)?.name;
  const uomLabel = (uid) => {
    const u = units.find((x) => x.id === uid);
    return u ? `${u.name} (${u.code})` : null;
  };

  const handleEdit = async (formPayload) => {
    setSaving(true);
    try {
      const payload = toUpdatePayload(item, formPayload);
      const updated = await catalogApi.updateItem(item.id, payload);
      setItem(updated);
      toast.success(t('Item updated'));
      setEditOpen(false);
    } catch (err) {
      toast.error(t('Could not update item'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  const saveParLevels = async (parLevels) => {
    const payload = toUpdatePayload(item, { parLevels });
    const updated = await catalogApi.updateItem(item.id, payload);
    setItem(updated);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await catalogApi.removeItem(item.id);
      toast.success(t('Item deleted'));
      navigate('/catalog');
    } catch (err) {
      toast.error(t('Could not delete item'), { description: err?.message });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <PageContainer size="full">
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={28} /></div>
      </PageContainer>
    );
  }

  if (error || !item) {
    return (
      <PageContainer size="full">
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load item")}
          description={error || t('Item not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/catalog')}>{t('Back to catalog')}</Button>}
        />
      </PageContainer>
    );
  }

  const sm = itemStatusMeta(item.status);

  const overviewItems = [
    { term: t('SKU'), description: item.sku || '—' },
    { term: t('Barcode'), description: item.barcode || '—' },
    { term: t('Category'), description: catName(item.categoryId) || '—' },
    { term: t('Stock unit'), description: uomLabel(item.stockUomId) || '—' },
    { term: t('Stock tracked'), description: item.isStockTracked ? t('Yes') : t('No') },
    { term: t('Tax / VAT rate'), description: item.taxRatePercent != null ? `${item.taxRatePercent}%` : '—' },
    { term: t('Standard cost'), description: money(item.standardCost) },
    { term: t('Default cost centre'), description: item.defaultCostCenter || '—' },
    { term: t('Default GL account'), description: item.defaultGlAccountCode || '—' },
  ];

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/catalog')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Catalog')}
      </Button>

      <PageHeader
        title={item.name}
        description={
          <span className="item-detail__sub">
            <Badge tone={sm.tone} dot>{t(sm.label)}</Badge>
            {item.sku && <span>{item.sku}</span>}
            {catName(item.categoryId) && <span>· {catName(item.categoryId)}</span>}
          </span>
        }
        icon={<Icon.Package size={22} />}
        actions={
          canManage ? (
            <>
              <Button variant="primary" leadingIcon={<Icon.Pencil size={15} />} onClick={() => setEditOpen(true)}>{t('Edit')}</Button>
              <IconButton aria-label={t('Delete item')} variant="ghost" onClick={() => setDeleteOpen(true)}><Icon.Trash size={16} /></IconButton>
            </>
          ) : null
        }
        tabs={<Tabs tabs={TABS.map((x) => ({ ...x, label: t(x.label) }))} value={tab} onChange={setTab} ariaLabel={t('Item sections')} />}
      />

      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onImageSelected} />

      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {stats && (
            <div className="item-kpis">
              <KpiTile label={t('Suppliers')} value={stats.supplierCount} meta={t('{count} active', { count: stats.activeSupplierCount })} />
              <KpiTile label={t('Best price')} value={money(stats.lowestPrice)} meta={stats.preferredPrice ? t('preferred {price}', { price: money(stats.preferredPrice) }) : undefined} />
              <KpiTile label={t('Price range')} value={priceRange(stats.lowestPrice, stats.highestPrice)} />
              <KpiTile label={t('Total purchased')} value={stats.totalQuantityPurchased || 0} meta={t('{count} purchases', { count: stats.purchaseCount })} />
              <KpiTile label={t('Avg unit price')} value={money(stats.averageUnitPrice)} />
              <KpiTile label={t('Total spend')} value={money(stats.totalSpend)} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Card>
              <Card.Header><Card.Title>{t('Image')}</Card.Title></Card.Header>
              <Card.Body>
                <div className="item-img-block">
                  {imageUrl ? (
                    <img className="item-img" src={imageUrl} alt={item.name} />
                  ) : (
                    <div className="item-img item-img--placeholder"><Icon.Package size={32} /></div>
                  )}
                  {canManage && (
                    <div className="item-img-actions">
                      <Button variant="secondary" size="sm" loading={imageBusy} leadingIcon={<Icon.Upload size={14} />} onClick={onPickImage}>
                        {imageUrl ? t('Replace') : t('Upload')}
                      </Button>
                      {imageUrl && (
                        <Button variant="ghost" size="sm" loading={imageBusy} leadingIcon={<Icon.Trash size={14} />} onClick={removeImage}>
                          {t('Remove')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
            <Card style={{ flex: '1 1 360px' }}>
              <Card.Body>
                <DescriptionList items={overviewItems} columns={2} />
              </Card.Body>
            </Card>
          </div>
          {item.description && (
            <Card>
              <Card.Header><Card.Title>{t('Description')}</Card.Title></Card.Header>
              <Card.Body>{item.description}</Card.Body>
            </Card>
          )}
          {item.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {item.tags.map((tag) => <Badge key={tag} tone="neutral">{tag}</Badge>)}
            </div>
          )}
        </div>
      )}

      {tab === 'suppliers' && (
        <ItemSuppliersTab itemId={item.id} suppliers={suppliers} units={units} canManage={canManage} onChanged={() => loadStats(item.id)} />
      )}

      {tab === 'purchases' && (
        <PurchaseHistoryTab item={item} suppliers={suppliers} locations={locations} canManage={canManage} onChanged={() => loadStats(item.id)} />
      )}

      {tab === 'parlevels' && (
        <ParLevelsTab item={item} locations={locations} canManage={canManage} onSave={saveParLevels} />
      )}

      <Drawer open={editOpen} onClose={() => (saving ? null : setEditOpen(false))} title={t('Edit item')} size="md">
        <ItemForm item={item} categories={categories} units={units} costCenters={costCenters} glAccounts={glAccounts} submitting={saving} onSubmit={handleEdit} onCancel={() => setEditOpen(false)} />
      </Drawer>

      <ConfirmDialog
        open={deleteOpen}
        title={t('Delete item')}
        description={t('Delete "{name}"? This also removes its supplier price-list links. This cannot be undone.', { name: item.name })}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => (deleting ? null : setDeleteOpen(false))}
      />
    </PageContainer>
  );
}
